import { Popover } from 'antd';
import { TextAreaRef } from 'antd/lib/input/TextArea';
import { TooltipPlacement } from 'antd/lib/tooltip';
import React, { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { DateValue } from 'components/atoms/due-date-picker';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import {
  NewTaskForm,
  StartAndDueDate,
} from 'components/molecules/new-task-modal/form';
import type {
  NewTaskValueSetterProp,
  NewTaskCardExtraDetails,
} from 'components/molecules/new-task-modal/form';
import { isRelatedEl } from 'components/molecules/new-task-popup/is-related-element';
import { Option } from 'components/molecules/squad-picker/types';
import { useGenerateAssigneePickerOptions } from 'hooks/use-assignee-options';
import { callWorkerWithCleanup } from 'utils/call-worker-with-cleanup';
import { NewTasksDefaultParamsGetterCallbackType } from './type';

export const PopupContent = styled.div`
  width: 350px;
`;
export const CreateTaskPopover: FC<{
  visible: boolean;
  toggle: (value: boolean) => void;
  placement?: TooltipPlacement;
  getNewTaskParams: () => ReturnType<NewTasksDefaultParamsGetterCallbackType>;
}> = ({ toggle, visible, placement, getNewTaskParams }) => {
  const [title, setTitle] = useState('');
  const inputRef = useRef<TextAreaRef>(null);
  const [assignee, setAssignee] = useState<Option[]>([]);

  const [dueDate, setDueDate] = useState<DateValue>();
  const [startDate, setStartDate] = useState<DateValue>();
  const [extraDetails, setExtraDetails] = useState<NewTaskCardExtraDetails>();
  const [description, setDescription] = useState('');
  const { generateAssigneePickerOptions } =
    useGenerateAssigneePickerOptions(true);
  const worker = callWorkerWithCleanup();
  const ref = useRef<NewTaskValueSetterProp>(null);
  const [startAndDueDate, setStartAndDueDate] = useState<StartAndDueDate>([
    null,
    null,
  ]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    const value = getNewTaskParams();

    const updateAssignee = async () => {
      if (!value.assignee) {
        return;
      }
      const users = await worker.workerApi.getUsersByIds(
        value.assignee.users.map((u) => u.userId),
      );

      setAssignee(
        (
          generateAssigneePickerOptions({
            users: Object.values(users),
            roles: [],
            teams: [],
            usersNotInProject: [],
          }) as any
        ).find((a) => a.id === 'users')?.options,
      );
    };

    const updateDueDate = async () => {
      if (!value.startDateAndDueDate?.[1]) {
        return;
      }
      setDueDate(value.startDateAndDueDate[1]);
      setStartAndDueDate(value.startDateAndDueDate);
    };
    const updateProject = async () => {
      setTimeout(() => {
        // ref.current wuld be set in next render
        if (!value.project) {
          return;
        }
        ref.current?.setProject(value.project);
      }, 10);
    };

    updateAssignee();
    updateDueDate();
    updateProject();
    setExtraDetails({
      fields: value.fields,
      projectPhase: value.projectPhase,
      defaultTimeEntryCategory: value.defaultTimeEntryCategory,
    });
  }, [
    generateAssigneePickerOptions,
    getNewTaskParams,
    visible,
    worker.workerApi,
  ]);

  const hasFormData = () =>
    (title && assignee.length > 0) ||
    dueDate ||
    startDate ||
    description.length > 0;

  const cleanupForm = () => {
    setTitle('');
    setAssignee([]);
    setDueDate(undefined);
    setStartDate(undefined);
    setDescription('');
  };

  const closeForm = () => {
    cleanupForm();
    toggle(false);
  };

  useEffect(() => {
    window.setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const [taskDescription, setTaskDescription] = useState<string>('');
  const [taskName, setTaskName] = useState<string>('');

  return (
    <Popover
      visible={visible}
      destroyTooltipOnHide
      trigger="click"
      placement={placement || 'right'}
      content={
        <PopupContent>
          <OutsideClickHandler
            onOutsideClick={() => {
              if (!hasFormData()) {
                closeForm();
              }
            }}
            allowed={isRelatedEl}
          >
            <NewTaskForm
              taskName={taskName}
              setTaskName={setTaskName}
              taskDescription={taskDescription}
              setTaskDescription={setTaskDescription}
              assignee={assignee}
              setAssignee={setAssignee}
              startAndDueDate={startAndDueDate}
              setStartAndDueDate={setStartAndDueDate}
              onSuccess={closeForm}
              onClose={closeForm}
              ref={ref}
              extraDetails={extraDetails}
              forceCheckAssigneeOnCreate
            />
          </OutsideClickHandler>
        </PopupContent>
      }
    />
  );
};
